import { EmptyState, Skeleton } from '@wundo/ui';
import type { UserOverview } from '@/types/messages.ts';
import { personName } from './personName.ts';
import { TryAgain } from './TryAgain.tsx';

export interface TranscriptStatusProps {
  /** The person asked for in the link. */
  userId: string | null;
  /** That person, once the list has them; null while it loads or when they are not in it. */
  person: UserOverview | null;
  peopleLoading: boolean;
  failed: boolean;
  loading: boolean;
  /** The person's conversations are in, and there are none. */
  noConversations: boolean;
  /** There are conversations, but nothing in the one being read to draw. */
  noMessages: boolean;
  onRetry: () => void;
}

/** What the transcript pane says while it has no messages to draw. */
export function TranscriptStatus({
  userId,
  person,
  peopleLoading,
  failed,
  loading,
  noConversations,
  noMessages,
  onRetry,
}: TranscriptStatusProps) {
  return (
    <>
      {!person && userId && peopleLoading && <TranscriptSkeleton />}

      {!userId && (
        <EmptyState
          title='Welcome to Messages'
          description='Select a user to start viewing messages'
        />
      )}

      {person && failed && (
        <EmptyState
          data-ui='error-state'
          title='Could not load the messages'
          description={`Something went wrong while loading ${personName(person)}'s conversations.`}
          action={<TryAgain onClick={onRetry} />}
        />
      )}

      {loading && <TranscriptSkeleton />}

      {person && noConversations && (
        <EmptyState
          data-ui='empty-state'
          title='No messages yet'
          description={`${personName(person)} has not talked to Wundo yet.`}
        />
      )}

      {person && !loading && !failed && noMessages && (
        <EmptyState title='No messages in this conversation yet' />
      )}
    </>
  );
}

/* A transcript on its way: bubbles on both sides, roughly the size messages are. */
function TranscriptSkeleton() {
  return (
    <div
      data-ui='transcript-loading'
      aria-busy='true'
      aria-label='Loading messages'
      className='flex flex-col gap-3.5'
    >
      <Skeleton className='h-12 w-1/2 self-end rounded-lg' />
      <div className='flex gap-2.5'>
        <Skeleton className='size-6.5 shrink-0' />
        <Skeleton className='h-24 w-2/3 rounded-lg' />
      </div>
      <Skeleton className='h-12 w-2/5 self-end rounded-lg' />
      <div className='flex gap-2.5'>
        <Skeleton className='size-6.5 shrink-0' />
        <Skeleton className='h-18 w-3/5 rounded-lg' />
      </div>
    </div>
  );
}
